import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlags } from '../../hooks/useOrgFeatureFlags';
import { apiService } from '../../services/api';
import { integrationApiService } from '../../services/integrationApi';
import type { IntegrationConfig, IntegrationSettings } from '../../services/integrationApi';
import {
  DataSyncSection,
  SyncModeSection,
  TagsAutomationSection,
  normalizeIntegrationSettings,
  useShopifyScopeControls,
} from '../IntegrationSettingsSections';
import RebateDiscountChoice from '../RebateDiscountChoice';
import {
  RebateTreatmentChoice,
  saveRebateTreatment,
  useRebateTreatmentSetting,
  REBATE_TREATMENT_QUERY_KEY,
} from '../RebateTreatmentChoice';
import type { RebateCheckoutTreatment } from '../RebateTreatmentChoice';
import ShopifyPricingZonesSection from '../ShopifyPricingZonesSection';
import ShopifySetupChecklist from '../ShopifySetupChecklist';
import LightspeedSetupChecklist from '../lightspeed/LightspeedSetupChecklist';
import LightspeedCatalogMatch from '../lightspeed/LightspeedCatalogMatch';
import LightspeedEcomScanCard from '../lightspeedEcom/LightspeedEcomScanCard';
import LightspeedEcomSettingsSection, {
  LIGHTSPEED_ECOM_PUSH_DEFAULTS,
  LIGHTSPEED_ECOM_PUSH_KEYS,
} from '../lightspeedEcom/LightspeedEcomSettingsSection';
import ShopifyCatalogScan from '../ShopifyCatalogScan';
import { rebateDiscountOn, runRebateDiscountSyncs } from './rebateDiscountSync';
import { walkthroughStepsFor } from './walkthroughSteps';
import LightspeedMappingPanel from '../lightspeed/LightspeedMappingPanel';
import { LightspeedDiscountChoice } from '../lightspeed/LightspeedSettingsSections';
import type { WalkthroughStep } from './walkthroughSteps';

export interface IntegrationSetupWalkthroughProps {
  open: boolean;
  integration: IntegrationConfig | null;
  /** Dialog dismissed, or "Skip for now" (which records `skippedAt` first). */
  onClose: () => void;
  /** All choices saved and the walkthrough recorded as completed. */
  onFinished: () => void;
}

const T = 'integrations.setupWalkthrough';

/**
 * The settings a connection does not ask about, in one guided flow. Choices are
 * held locally and saved once on Finish; each "Next" only advances.
 */
const IntegrationSetupWalkthrough: React.FC<IntegrationSetupWalkthroughProps> = ({
  open,
  integration,
  onClose,
  onFinished,
}) => {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { flags, isEnabled } = useOrgFeatureFlags();

  const [settings, setSettings] = useState<IntegrationSettings | null>(null);
  const [orderSyncEnabled, setOrderSyncEnabled] = useState(false);
  const [treatment, setTreatment] = useState<RebateCheckoutTreatment | ''>('');
  const [stepIndex, setStepIndex] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scope = useShopifyScopeControls(integration);
  const integrationId = integration?.id ?? null;
  const platform = integration?.platform ?? 'shopify';
  const isShopify = platform === 'shopify';
  const isLightspeedEcom = platform === 'lightspeed_ecom';

  // Start over only for a different integration or a fresh open, never when the
  // integration list refetches underneath a half-answered walkthrough.
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !integration) {
      seededRef.current = null;
      return;
    }
    if (seededRef.current === integration.id) return;
    seededRef.current = integration.id;
    setSettings(normalizeIntegrationSettings(integration.settings));
    setOrderSyncEnabled(integration.orderSyncEnabled === true);
    setStepIndex(0);
    setMaxStep(0);
    setError(null);
    setBusy(false);
  }, [open, integration]);

  const writeDiscountsLocked = integration?.scopeStatus?.settingLocks?.writeDiscounts?.locked === true;
  const steps: WalkthroughStep[] = useMemo(
    () =>
      integration
        ? walkthroughStepsFor({
            platform: integration.platform,
            flags: flags ?? {},
            scopeLocks: { writeDiscounts: writeDiscountsLocked },
          })
        : [],
    [integration, flags, writeDiscountsLocked],
  );
  const step = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
  const rebatesStep = steps.find((s) => s.id === 'rebates')?.rebates;

  const treatmentQuery = useRebateTreatmentSetting(open && rebatesStep?.treatment === true);
  const savedTreatment = treatmentQuery.data?.treatment ?? null;
  useEffect(() => {
    if (savedTreatment) setTreatment(savedTreatment);
  }, [savedTreatment]);

  const perIntegrationSelection = isEnabled('per_integration_sync_selection');
  const selectionQuery = useQuery({
    queryKey: ['setup-walkthrough-selection', integrationId, perIntegrationSelection],
    queryFn: () => apiService.getStoreSyncSelection(perIntegrationSelection ? integrationId : undefined),
    enabled: open && !!integrationId && step?.id === 'products',
  });

  if (!integration || !settings || !step) return null;

  const change = (partial: Partial<IntegrationSettings>) => setSettings((s) => (s ? { ...s, ...partial } : s));
  const isLast = stepIndex === steps.length - 1;
  const completedIds = () => steps.slice(0, maxStep + 1).map((s) => s.id);

  const next = () => {
    const to = Math.min(stepIndex + 1, steps.length - 1);
    setStepIndex(to);
    setMaxStep((m) => Math.max(m, to));
  };

  const skip = async () => {
    setBusy(true);
    setError(null);
    try {
      await integrationApiService.updateSetupWalkthrough(integration.id, {
        completedSteps: steps.slice(0, maxStep).map((s) => s.id),
        skipped: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(`${T}.saveError`));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      // One save for everything the store chose. The settings go first so the
      // discount sync a treatment change triggers already sees the new switches.
      // The Mapping tab owns the layout and field maps; leaving them out keeps
      // this save from writing back a copy seeded when the walkthrough opened.
      const {
        storeLayout: _storeLayout,
        specMetafields: _specMetafields,
        lightspeedLayout: _lightspeedLayout,
        lightspeedCustomFields: _lightspeedCustomFields,
        ...ownSettings
      } = { ...integration.settings, ...settings };
      await integrationApiService.updateIntegration(integration.id, {
        settings: ownSettings as IntegrationSettings,
      });
      if (orderSyncEnabled !== (integration.orderSyncEnabled === true)) {
        await integrationApiService.updateOrderSync(integration.id, orderSyncEnabled);
      }
      if (rebatesStep?.treatment && treatment && treatment !== savedTreatment) {
        await saveRebateTreatment(treatment, t('store.rebateTreatment.saveError'));
        queryClient.invalidateQueries({ queryKey: REBATE_TREATMENT_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ['store-rebate-programs'] });
      }
      if (isShopify) {
        await runRebateDiscountSyncs(integration.id, integration.settings.rebateDiscounts, settings.rebateDiscounts, {
          instant: rebatesStep?.instant === true,
          bundle: rebatesStep?.bundle === true,
        });
      }
      if (
        rebatesStep?.lightspeedInstant &&
        rebateDiscountOn(integration.settings.rebateDiscounts, 'instant') !== rebateDiscountOn(settings.rebateDiscounts, 'instant')
      ) {
        await integrationApiService.syncLightspeedRebateDiscounts(integration.id).catch(() => null);
      }
      await integrationApiService.updateSetupWalkthrough(integration.id, {
        completedSteps: completedIds(),
        completed: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
      onFinished();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(`${T}.saveError`));
    } finally {
      setBusy(false);
    }
  };

  const yesNo = (value: boolean) => t(value ? `${T}.summary.on` : `${T}.summary.off`);
  const treatmentLabel = treatmentQuery.data?.options.find((o) => o.value === treatment)?.label;
  const summary: Array<[string, string]> = [];
  if (steps.some((s) => s.id === 'sync-mode')) {
    summary.push([t(`${T}.summary.syncMode`), t(`${T}.summary.modes.${isShopify && settings.syncMode === 'link_only' ? 'link_only_shopify' : settings.syncMode || 'override'}`)]);
    if (platform === 'lightspeed' && settings.syncMode === 'link_only') {
      const pricesOn = isEnabled('lightspeed_catalog_parity')
        ? settings.linkOnlyPricing !== false
        : settings.linkOnlyPricing === true;
      summary.push([t(`${T}.summary.linkOnlyPricing`), yesNo(pricesOn)]);
    }
  }
  if (isLightspeedEcom) {
    // Content-only: the summary lists the eCom push fields, never price, images or stock.
    for (const key of LIGHTSPEED_ECOM_PUSH_KEYS) {
      summary.push([
        t(`integrations.lightspeedEcom.settings.fields.${key}.label`),
        yesNo(settings.lightspeedEcom?.[key] ?? LIGHTSPEED_ECOM_PUSH_DEFAULTS[key]),
      ]);
    }
  } else {
    summary.push(
      [t(`${T}.summary.products`), yesNo(settings.syncProducts !== false)],
      [t(`${T}.summary.pricing`), yesNo(settings.syncPricing !== false)],
      [t(`${T}.summary.images`), yesNo(settings.syncImages !== false)],
      [t(`${T}.summary.inventory`), yesNo(settings.syncInventory === true)],
    );
  }
  if (isShopify) summary.push([t(`${T}.summary.orderSync`), yesNo(orderSyncEnabled)]);
  if (rebatesStep?.treatment && treatmentLabel) summary.push([t(`${T}.summary.treatment`), treatmentLabel]);
  if (rebatesStep?.instant && !rebatesStep.discountsLocked) {
    summary.push([t(`${T}.summary.instantDiscounts`), yesNo(rebateDiscountOn(settings.rebateDiscounts, 'instant'))]);
  }
  if (rebatesStep?.bundle && !rebatesStep.discountsLocked) {
    summary.push([t(`${T}.summary.bundleDiscounts`), yesNo(rebateDiscountOn(settings.rebateDiscounts, 'bundle'))]);
  }
  if (rebatesStep?.lightspeedInstant) {
    summary.push([t(`${T}.summary.registerDiscounts`), yesNo(rebateDiscountOn(settings.rebateDiscounts, 'instant'))]);
  }
  summary.push(
    [t(`${T}.summary.motzuTag`), yesNo(settings.addMotzuTag !== false)],
    [
      t(`${T}.summary.autoSync`),
      settings.autoSync ? t(`${T}.summary.intervals.${settings.syncInterval}`) : t(`${T}.summary.off`),
    ],
    [t(`${T}.summary.autoSyncNewProducts`), yesNo(settings.autoSyncNewProducts === true)],
  );

  const selectionCount = selectionQuery.data?.selectedProductIds?.length ?? 0;
  const productsUrl = perIntegrationSelection
    ? `/app/products?integrationId=${encodeURIComponent(integration.id)}`
    : '/app/products';

  const renderBody = () => {
    switch (step.id) {
      case 'catalog-match':
        if (isLightspeedEcom) return <LightspeedEcomScanCard integration={integration} />;
        return isShopify ? (
          <ShopifyCatalogScan integration={integration} syncMode={settings.syncMode} settings={settings} compact />
        ) : (
          <LightspeedCatalogMatch integrationId={integration.id} compact />
        );
      case 'sync-mode':
        return (
          <SyncModeSection
            settings={settings}
            onChange={change}
            modes={step.modes}
            copyVariant={platform === 'lightspeed' ? 'lightspeed' : 'shopify'}
            linkOnlyPricingDefaultsOn={platform === 'lightspeed' && isEnabled('lightspeed_catalog_parity')}
          />
        );
      case 'data-sync':
        // The eCom connector never reads the generic switches (a stray `syncProducts: false`
        // would otherwise leave it looking off with no control to turn it back on).
        if (isLightspeedEcom) return <LightspeedEcomSettingsSection settings={settings} onChange={change} />;
        return (
          <DataSyncSection
            settings={settings}
            onChange={change}
            platform={platform}
            scope={scope}
            orderSync={isShopify ? { enabled: orderSyncEnabled, onChange: setOrderSyncEnabled } : undefined}
          />
        );
      case 'products':
        return (
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selectionQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                {t('common.loading')}
              </Typography>
            ) : selectionQuery.isError ? (
              <Alert severity="info">{t(`${T}.products.countError`)}</Alert>
            ) : selectionQuery.data?.syncAll ? (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t(`${T}.products.all`)}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectionCount > 0
                  ? t(`${T}.products.count`, { count: selectionCount })
                  : t(`${T}.products.none`)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t(perIntegrationSelection ? `${T}.products.perIntegration` : `${T}.products.shared`)}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNew fontSize="small" />}
                component="a"
                href={productsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(`${T}.products.choose`)}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t(`${T}.products.later`)}
            </Typography>
          </Paper>
        );
      case 'rebates': {
        const r = step.rebates!;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {r.treatment && (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2">{t(`${T}.rebates.treatmentTitle`)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('store.rebateTreatment.intro')}
                </Typography>
                {treatmentQuery.isLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('common.loading')}
                  </Typography>
                ) : treatmentQuery.isError || !treatmentQuery.data ? (
                  <Alert severity="error">{t('store.rebateTreatment.loadError')}</Alert>
                ) : (
                  <RebateTreatmentChoice
                    setting={treatmentQuery.data}
                    value={treatment}
                    onChange={setTreatment}
                    hasShopify={platform === 'shopify'}
                    shopifyPlus={integration?.isShopifyPlus === true}
                  />
                )}
              </Paper>
            )}
            {(r.instant || r.bundle) &&
              (r.discountsLocked ? (
                <Alert severity="warning">
                  {scope.customApp
                    ? t('integrations.rebateDiscounts.customApp')
                    : t('integrations.rebateDiscounts.reconnect')}
                </Alert>
              ) : (
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {r.instant && (
                    <RebateDiscountChoice
                      kind="instant"
                      checked={rebateDiscountOn(settings.rebateDiscounts, 'instant')}
                      onChange={(instant) => change({ rebateDiscounts: { ...settings.rebateDiscounts, instant } })}
                      treatmentNote={
                        r.treatment && treatment !== 'discount_line'
                          ? t('integrations.rebateDiscounts.choice.instant.needsTreatment')
                          : null
                      }
                    />
                  )}
                  {r.instant && r.bundle && <Divider />}
                  {r.bundle && (
                    <RebateDiscountChoice
                      kind="bundle"
                      checked={rebateDiscountOn(settings.rebateDiscounts, 'bundle')}
                      onChange={(bundle) => change({ rebateDiscounts: { ...settings.rebateDiscounts, bundle } })}
                    />
                  )}
                </Paper>
              ))}
            {r.lightspeedInstant && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <LightspeedDiscountChoice
                  checked={rebateDiscountOn(settings.rebateDiscounts, 'instant')}
                  onChange={(instant) => change({ rebateDiscounts: { ...settings.rebateDiscounts, instant } })}
                />
              </Paper>
            )}
          </Box>
        );
      }
      case 'categories':
        return <LightspeedMappingPanel integration={integration} showChanges={false} />;
      case 'zones':
        return <ShopifyPricingZonesSection integrationId={integration.id} />;
      case 'automation':
        return <TagsAutomationSection settings={settings} onChange={change} platform={platform} syncTimeZone={integration?.syncTimeZone} lastSyncSlotAt={integration?.syncSchedule?.lastSyncSlotAt} compact />;
      case 'finish':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
              {summary.map(([label, value], idx) => (
                <React.Fragment key={label}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                      {value}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Paper>
            {isShopify && (
              <ShopifySetupChecklist
                shopId={integration.credentials?.shopId ? String(integration.credentials.shopId) : null}
                integrationId={integration.id}
              />
            )}
            {platform === 'lightspeed' && <LightspeedSetupChecklist integrationId={integration.id} />}
            <Typography variant="caption" color="text.secondary">
              {t(`${T}.finish.note`)}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ display: 'block' }}>
          {t(`${T}.title`, { name: integration.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(`${T}.subtitle`)}
        </Typography>
      </DialogTitle>
      <Box sx={{ px: 3, pb: 1 }}>
        <Stepper
          activeStep={stepIndex}
          alternativeLabel
          sx={{
            display: { xs: 'none', sm: 'flex' },
            '& .MuiStepLabel-label': { fontSize: '0.75rem' },
          }}
        >
          {steps.map((s, idx) => (
            <Step key={s.id} completed={idx < stepIndex}>
              <StepLabel>{t(`${T}.steps.${s.i18nKey}.label`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
          {t(`${T}.stepOf`, { current: stepIndex + 1, total: steps.length })}
        </Typography>
      </Box>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t(`${T}.steps.${step.i18nKey}.title`)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`${T}.steps.${step.i18nKey}.body`)}
            </Typography>
            {step.id === 'sync-mode' && isShopify && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t(`${T}.steps.syncMode.pricingOnlyHint`)}
              </Typography>
            )}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {renderBody()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={skip} disabled={busy} color="inherit">
          {t(`${T}.skip`)}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={busy || stepIndex === 0}>
          {t('common.back')}
        </Button>
        {isLast ? (
          <Button variant="contained" onClick={finish} disabled={busy}>
            {busy ? t(`${T}.saving`) : t(`${T}.finish.button`)}
          </Button>
        ) : (
          <Button variant="contained" onClick={next} disabled={busy}>
            {t('common.next')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationSetupWalkthrough;
